"use client";

import { useEffect, useState } from "react";
import { differenceInMinutes, format } from "date-fns";
import { EVENTS_API } from "@/lib/apis";
import Link from "next/link";

type UpcomingEventProps = {
	communitySlug: string;
};

const UpcomingEvent = ({ communitySlug }: UpcomingEventProps) => {
	const [upcomingEvent, setUpcomingEvent] = useState<{ title: string; dateTime: string } | null>(
		null
	);
	const [timeLeft, setTimeLeft] = useState<string>("");

	useEffect(() => {
		const fetchUpcomingEvent = async () => {
			try {
				const response = await fetch(`${EVENTS_API}?community-slug=${communitySlug}`, {
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
					},
				});
				const data = await response.json();

				if (!response.ok) throw new Error(data.error || "Failed to fetch events");

				const events = data?.data?.events || [];
				const now = new Date();
				const upcoming = events.find(
					(event: { dateTime: string }) => new Date(event.dateTime) > now
				);

				if (upcoming) {
					setUpcomingEvent({ title: upcoming.title, dateTime: upcoming.dateTime });
					updateTimeLeft(upcoming.dateTime);
				} else {
					setUpcomingEvent(null);
				}
			} catch (error) {
				console.error("Error fetching upcoming event:", error);
			}
		};

		fetchUpcomingEvent();
		const interval = setInterval(fetchUpcomingEvent, 60000);

		return () => clearInterval(interval);
	}, [communitySlug]);

	const updateTimeLeft = (eventDateTime: string) => {
		const eventTime = new Date(eventDateTime);
		const minutesLeft = differenceInMinutes(eventTime, new Date());

		if (minutesLeft > 0) {
			const days = Math.floor(minutesLeft / 1440);
			const hours = Math.floor((minutesLeft % 1440) / 60);
			const minutes = minutesLeft % 60;

			if (days > 0) {
				setTimeLeft(`${days}d ${hours}h`);
			} else {
				setTimeLeft(`${hours}h ${minutes}m`);
			}
		} else {
			setTimeLeft("Starting now!");
		}
	};

	if (!upcomingEvent) return null;

	const eventDate = format(new Date(upcomingEvent.dateTime), "yyyy-MM-dd");

	return (
		<Link
			href={`/communities/${communitySlug}/calendar?date=${eventDate}`}
			className="mx-auto flex items-center justify-center pb-4 text-blue-900"
		>
			<p className="font-semibold">
				📅 {upcomingEvent.title} happening in {timeLeft}
			</p>
		</Link>
	);
};

export default UpcomingEvent;
